// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Components
import { Button } from '@/common-utils/components/ui/button';
import { Input } from '@/common-utils/components/ui/input';
import Entity from '../../entity/Entity';
import ICONS from '@/common-utils/enums/icons';
import useDeviceSizeStore from '@/app/store/deviceSize';

/**
 * @param root0
 * @param root0.table
 * @param root0.pagination
 * @param root0.pageUpdate
 * @param root0.dataFetchingIsLoading
 * @param root0.resetPagination
 * @param root0.setResetPagination
 * @param root0.currentPageCount
 * @param root0.selectionMode
 * @param root0.query
 * @returns {JSX} CustomPagination
 */
function CustomPagination({ table, query, pagination, pageUpdate, dataFetchingIsLoading, resetPagination, setResetPagination, currentPageCount, selectionMode = false }) {

    // Constants
    const pageCount = currentPageCount.pageCount;

    // State
    const [currentPageNumber, setCurrentPageNumber] = useState(pagination.pageIndex + 1);

    //Store
    const isLargeDevice = useDeviceSizeStore(state => state.isLargeDevice);

    // Hooks
    useEffect(() => {
        if (resetPagination) {
            setCurrentPageNumber(pagination.pageIndex + 1);
            setResetPagination(false);
        }
    }, [resetPagination]);

    useEffect(() => {
        setCurrentPageNumber(pagination.pageIndex + 1);
    }, [pagination.pageIndex]);

    // Functions
    const goToPage = (pageNumber) => {
        const newPageIndex = Math.min(Math.max(pageNumber, 1), pageCount) - 1;

        // Compare with the last fetched page
        if (newPageIndex !== Math.floor(query.offset / pagination.pageSize)) {
            pageUpdate(true, newPageIndex * pagination.pageSize);
        }
    };

    // Hook
    // Adjust current page number if pageCount changes
    useEffect(() => {
        if (pageCount > 0 && currentPageNumber > pageCount) {
            setCurrentPageNumber(pageCount);
            goToPage(pageCount);
        }
    }, [pageCount]);

    // Render
    return (
        <div id='table-pagination' className='flex items-center justify-end space-x-2 pt-4 pb-1'>
            {
                selectionMode &&
                <Entity
                    className={'flex-1 text-sm text-muted-foreground'}
                    entity={'table.pagination.rowSelected'}
                    data={{ rowSelected: Object.keys(table.getState().rowSelection).length, rowCount: currentPageCount.rowCount }}
                />
            }
            <div className='space-x-2 flex items-center'>
                <Button
                    variant='secondary'
                    size='sm'
                    entity={isLargeDevice ? 'button.previous' : undefined}
                    icon={!isLargeDevice ? ICONS.CHEVRON_LEFT : undefined}
                    onClick={() => {
                        const newPage = Math.max(currentPageNumber - 1, 1);
                        setCurrentPageNumber(newPage);
                        goToPage(newPage);
                    }}
                    disabled={currentPageNumber === 1 || currentPageNumber === '' || currentPageNumber === '0' || dataFetchingIsLoading}
                />
                <Button
                    variant='secondary'
                    size='sm'
                    entity='1'
                    onClick={() => {
                        if (currentPageNumber !== 1) {
                            setCurrentPageNumber(1);   // immediate UI feedback
                            goToPage(1);
                        }
                    }}
                    disabled={dataFetchingIsLoading}
                />
                <Input
                    type='text'
                    className='text border border-secondary-foreground max-h-9'
                    style={{ width: `${String(currentPageNumber || '').length + 4}ch` }}
                    data-cy='page-jump-input'
                    disabled={dataFetchingIsLoading}
                    value={currentPageNumber}
                    onFocus={() => {
                        setCurrentPageNumber('');
                    }}
                    onChange={(event) => {
                        const regex = /\D+/gm;
                        const cleanedValue = event.target.value.replace(regex, '');
                        setCurrentPageNumber(parseInt(cleanedValue));
                    }}
                    onBlur={() => {
                        // When user's lost focus on this input :

                        // If input has its value like '' or '0', set pageIndex to 0 and input value to 1
                        if (currentPageNumber === '' || currentPageNumber === '0') {
                            pageUpdate(true, 0);
                            setCurrentPageNumber(1);
                        }
                        // If input has its value beyond the number of pages, set pageIndex to the last page index number and input value to the last page number
                        else if (currentPageNumber > pageCount) {
                            pageUpdate(true, (pageCount - 1) * pagination.pageSize);
                            setCurrentPageNumber(parseInt(pageCount));
                        }
                        // Else set pageIndex to the current page index number and input value to the current page number
                        else {
                            pageUpdate(true, (currentPageNumber - 1) * pagination.pageSize);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.currentTarget.blur(); }
                    }}
                />
                <Button
                    variant='secondary'
                    size='sm'
                    entity={String(pageCount)}
                    onClick={() => {
                        if (currentPageNumber !== pageCount) {
                            setCurrentPageNumber(pageCount);   // immediate UI feedback
                            goToPage(pageCount);
                        }
                    }}
                    disabled={dataFetchingIsLoading}
                />
                <Button
                    variant='secondary'
                    size='sm'
                    entity={isLargeDevice ? 'button.next' : undefined}
                    icon={!isLargeDevice ? ICONS.MORE_INFO : undefined}
                    onClick={() => {
                        const newPage = Math.min(currentPageNumber + 1, pageCount);
                        setCurrentPageNumber(newPage);
                        goToPage(newPage);
                    }}
                    disabled={pageCount <= 1 || currentPageNumber === pageCount || currentPageNumber === '' || dataFetchingIsLoading}
                />
            </div>
        </div>
    );
}

CustomPagination.propTypes = {
    table: PropTypes.object.isRequired,
    query: PropTypes.object.isRequired,
    pagination: PropTypes.object.isRequired,
    pageUpdate: PropTypes.func.isRequired,
    dataFetchingIsLoading: PropTypes.bool.isRequired,
    resetPagination: PropTypes.bool.isRequired,
    setResetPagination: PropTypes.func.isRequired,
    currentPageCount: PropTypes.object.isRequired,
    selectionMode: PropTypes.bool
};
export default CustomPagination;